import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";

function ContactInboxPage() {
  const API = "/backend/public";
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState("10");
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/contacts-admin`, {
        params: { search, page, per_page: perPage },
      });
      const payload = res.data || {};
      setRows(payload.data || []);
      setMeta({
        current_page: payload.current_page || 1,
        last_page: payload.last_page || 1,
        total: payload.total || 0,
      });
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, perPage]);

  const allChecked = useMemo(
    () => rows.length > 0 && rows.every((item) => selectedIds.includes(item.id)),
    [rows, selectedIds]
  );

  const toggleAll = () => {
    if (allChecked) setSelectedIds([]);
    else setSelectedIds(rows.map((r) => r.id));
  };

  const toggleOne = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter((x) => x !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await axios.delete(`${API}/api/contacts/${id}`);
    fetchList();
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} messages?`)) return;
    await axios.post(`${API}/api/contacts/bulk-delete`, { ids: selectedIds });
    fetchList();
  };

  const openView = async (id) => {
    const res = await axios.get(`${API}/api/contacts/${id}`);
    setViewItem(res.data || null);
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h3 className="mb-0">Contact Us Inbox</h3>
          <span className="badge bg-light text-dark border">{meta.total} Messages</span>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Search by name/email/message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  fetchList();
                }
              }}
            />
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary w-100" onClick={() => { setPage(1); fetchList(); }}>Filter</button>
          </div>
          <div className="col-md-2">
            <select className="form-select" value={perPage} onChange={(e) => { setPerPage(e.target.value); setPage(1); }}>
              <option value="10">10 / page</option>
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
              <option value="all">View All</option>
            </select>
          </div>
          <div className="col-md-2">
            <button className="btn btn-danger w-100" disabled={!selectedIds.length} onClick={bulkDelete}>
              Delete Selected
            </button>
          </div>
        </div>

        <div className="table-responsive bg-white border rounded">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
                <th style={{ width: 70 }}>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Message</th>
                <th style={{ width: 140 }}>Date</th>
                <th style={{ width: 130 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-4">Loading...</td></tr>
              ) : rows.length ? (
                rows.map((item, idx) => (
                  <tr key={item.id}>
                    <td><input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleOne(item.id)} /></td>
                    <td>{(meta.current_page - 1) * (perPage === "all" ? rows.length : Number(perPage)) + idx + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.email}</td>
                    <td>{String(item.message || "").slice(0, 90)}{String(item.message || "").length > 90 ? "..." : ""}</td>
                    <td>{item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</td>
                    <td>
                      <button className="btn btn-sm btn-info me-1" onClick={() => openView(item.id)}><i className="bi bi-eye"></i></button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteOne(item.id)}><i className="bi bi-trash"></i></button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={7} className="text-center py-4">No messages found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {perPage !== "all" && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>Page {meta.current_page} of {meta.last_page}</div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" disabled={meta.current_page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={meta.current_page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        )}

        {viewItem && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={() => setViewItem(null)}
          >
            <div className="bg-white p-3 rounded" style={{ width: "min(720px, 92vw)" }} onClick={(e) => e.stopPropagation()}>
              <h5>Contact Message</h5>
              <div><b>Name:</b> {viewItem.name}</div>
              <div><b>Email:</b> {viewItem.email}</div>
              <div><b>Date:</b> {viewItem.created_at ? new Date(viewItem.created_at).toLocaleString() : "-"}</div>
              <hr />
              <div style={{ whiteSpace: "pre-wrap" }}>{viewItem.message}</div>
              <button className="btn btn-secondary mt-3" onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default ContactInboxPage;




